/**
 * Acting on several files at once.
 *
 * The walkthrough's complaint was small and constant: a session holds dozens
 * of photographs, and every correction - delete these six, mark those twelve
 * as the left side - was one card at a time.
 *
 * The rules it follows:
 *
 * **It is the same endpoint, once per file.** No batch route was added. The
 * server re-derives ownership for every id instead of trusting a list a client
 * sent, and one file's refusal does not decide the other nineteen.
 *
 * **A partial result is reported as one.** "18 dosya silindi, 2 dosya
 * silinemedi" with the reasons behind the two, not a success message over
 * silent failures. The files that failed stay selected, because retrying them
 * is the next thing the reader wants to do.
 *
 * **Deleting asks first, and says what it will destroy.** The same two-step
 * the single delete uses, with the count in it - there is no trash and no
 * undo.
 *
 * ## The lifecycle, and why it is not a queue
 *
 * Enhancing twelve photographs is the first bulk action here long enough to
 * need one. It has the five states the brief asks for - queued, running,
 * completed, failed, cancelled - and they are the states of *this* run rather
 * than rows in a table: each file is one owner-scoped request, the progress
 * count is how many the server has answered for, and Durdur stops the rest.
 *
 * That shape is what makes the delete-safety rule free rather than something
 * to engineer. A late enhancement cannot resurrect a deleted photograph or a
 * deleted project, because there is no late result: every call re-derives
 * ownership and a file that has gone answers 404, which lands in the failure
 * list beside the reason. A server-side queue would have had to be taught the
 * same thing.
 */

import { useEffect, useRef, useState } from "react";

import { assignGuidedCoverageRegion, deleteMedia, enhanceMedia, estimateProjectRegions, type RegionEstimateReport } from "@/api/client";
import type { SubjectType } from "@/api/types";
import { Button, Callout } from "@/components/ui";
import { GUIDED_REGIONS } from "@/features/coverage/guidedCoverage";
import {
  assignFailureText,
  deleteFailureText,
  enhanceFailureText,
  groupFailures,
  runBulk,
  type BulkResult,
} from "./bulkMedia";

type Report =
  | { kind: "deleted"; result: BulkResult; attempted: number }
  | { kind: "assigned"; result: BulkResult; attempted: number; label: string }
  | { kind: "enhanced"; result: BulkResult; attempted: number };

export function BulkMediaActions({
  selected,
  subjectType,
  projectId,
  onClear,
  enhanceable,
  onCompleted,
}: {
  /** The ids the reader has ticked, in list order. */
  selected: readonly string[];
  subjectType: SubjectType;
  projectId?: string;
  /**
   * The subset of `selected` the enhancement pipeline would actually act on,
   * decided from the same measurement the analysis screen counted.
   *
   * Passed in rather than derived here because the caller is what holds the
   * assessments. Empty means the action is offered as unavailable with the
   * reason, never hidden - a reader who ticked twelve blurry files and sees no
   * button learns nothing.
   */
  enhanceable: readonly string[];
  onClear: () => void;
  /**
   * The files the server actually changed. The screen drops these from the
   * selection and re-reads; anything that failed stays ticked.
   */
  onCompleted: (succeeded: string[]) => void;
}) {
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [region, setRegion] = useState("");
  const [busy, setBusy] = useState<null | "delete" | "assign" | "enhance" | "estimate">(null);
  const [estimateReport, setEstimateReport] = useState<RegionEstimateReport | null>(null);
  const [estimateError, setEstimateError] = useState<string | null>(null);
  const estimateSelected = async () => {
    if (!projectId || busy !== null) return;
    setBusy("estimate");
    setEstimateReport(null);
    setEstimateError(null);
    const ids = [...selected];
    try {
      const result = await estimateProjectRegions(projectId, undefined, ids, true, true);
      if (!mounted.current) return;
      if (!result.available) {
        setEstimateError("Bölge tahmin modeli şu anda kullanılamıyor. Tekrar deneyin.");
      } else {
        setEstimateReport(result);
        onCompleted([]);
      }
    } catch {
      if (mounted.current) setEstimateError("Bölge atama sonucu alınamadı. Dosyaları kontrol edip tekrar deneyin.");
    } finally {
      if (mounted.current) setBusy(null);
    }
  };
  const [progress, setProgress] = useState(0);
  const [report, setReport] = useState<Report | null>(null);
  //: Set when the reader presses Durdur. The files already sent still finish -
  //: they are requests in flight, not rows that can be rolled back - and
  //: nothing further is started.
  const stopper = useRef<AbortController | null>(null);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const regions = subjectType === "human_head" ? [] : (GUIDED_REGIONS[subjectType] ?? []);
  const count = selected.length;

  const run = async (
    kind: "delete" | "assign" | "enhance",
    ids: readonly string[],
    action: (id: string) => Promise<unknown>,
    describe: (result: BulkResult, attempted: number) => Report,
  ) => {
    setBusy(kind);
    setProgress(0);
    setReport(null);
    const controller = new AbortController();
    stopper.current = controller;
    const attempted = ids.length;
    const result = await runBulk([...ids], action, {
      signal: controller.signal,
      onProgress: (done) => {
        if (mounted.current) setProgress(done);
      },
    });
    stopper.current = null;
    if (!mounted.current) return;
    setBusy(null);
    setConfirmingDelete(false);
    setReport(describe(result, attempted));
    // Only what the server confirmed. A file that refused stays ticked.
    onCompleted(result.succeeded);
  };

  if (count === 0) return null;

  return (
    <div className="bulkbar" role="group" aria-label="Seçili dosyalar için işlemler">
      <div className="bulkbar__row">
        <strong>{count} dosya seçildi</strong>
        <Button onClick={onClear} disabled={busy !== null}>
          Seçimi temizle
        </Button>
      </div>

      {regions.length > 0 && (
        <div className="bulkbar__row">
          <label className="field bulkbar__field">
            <span>Seçilenleri hangi çekim açısına işaretleyelim?</span>
            <select
              value={region}
              disabled={busy !== null}
              onChange={(event) => setRegion(event.target.value)}
            >
              <option value="">Açı seçin</option>
              {projectId && <option value="__auto__">OTOMATİK BÖLGE ATAMA</option>}
              {regions.map((item) => (
                <option key={item.key} value={item.key}>
                  {item.label}
                </option>
              ))}
              {/* Removing a mark is as real an action as making one, and it
                  was previously only possible one card at a time. */}
              <option value="__none__">İşareti kaldır</option>
            </select>
          </label>
          <Button
            busy={busy === "assign" || busy === "estimate"}
            disabled={busy !== null || region === ""}
            onClick={() => {
              if (region === "__auto__") {
                void estimateSelected();
                return;
              }
              const value = region === "__none__" ? null : region;
              const label =
                value === null
                  ? "işaret kaldırıldı"
                  : (regions.find((item) => item.key === value)?.label ?? value);
              void run(
                "assign",
                selected,
                (id) => assignGuidedCoverageRegion(id, value),
                (result, attempted) => ({ kind: "assigned", result, attempted, label }),
              );
            }}
          >
            İşaretle
          </Button>
        </div>
      )}

      {region === "__auto__" && !estimateReport && (
        <p>Bulunan en olası açı, doğruluğu garanti edilmeden tahmini bölge olarak kaydedilir.
          Yanlış tahminleri değiştirebilirsiniz. Manuel atanmış bölgeler korunur.
          İşareti kaldırılmış fotoğraflar yeniden tahmin edilir.</p>
      )}
      {busy === "estimate" && <p role="status">Seçili fotoğraflar inceleniyor · Otomatik Bölge Atama çalışıyor…</p>}
      {estimateError && <p role="alert">{estimateError}</p>}
      {estimateReport && (
        <Callout tone={estimateReport.assigned > 0 && estimateReport.review_required === 0 ? "success" : "caution"} title="Otomatik Bölge Atama sonucu">
          <p>{estimateReport.assigned} otomatik atandı · {estimateReport.review_required} Kontrol gerekli.</p>
          {(estimateReport.tentative ?? 0) > 0 && <p>{estimateReport.tentative} fotoğrafa tahmini bölge yazıldı. Tahminler yanlış olabilir; fotoğraf kartından değiştirebilirsiniz.</p>}
          {estimateReport.auto_assignment_enabled === false && <p>%95 doğruluk koşulu doğrulanmadı. Tahmini bölgeler doğrulanmış atama değildir.</p>}
          {estimateReport.anchors_missing && <p>Karşılaştırma için en az iki farklı açıdan işaretli referans fotoğraf gerekiyor. Tahmin çalıştırılmadı.</p>}
          {(estimateReport.protected ?? 0) > 0 && <p>{estimateReport.protected} fotoğraf manuel veya çekim sırasında verilen karar korunduğu için atlandı. Manuel kaldırılan işaretler de korunur.</p>}
          {estimateReport.evaluated !== undefined && <p>{estimateReport.evaluated} fotoğraf için tahmin çalıştırıldı.</p>}
          <p>Manuel işaretlenen fotoğraflar korunur.</p>
        </Callout>
      )}

      {/*
        The measured, deterministic pipeline - the one that works. It runs only
        on the files a measurement says it can help, so the batch cannot
        include a file the single-file path would refuse with a 409.
      */}
      <div className="bulkbar__row">
        <Button
          busy={busy === "enhance"}
          disabled={busy !== null || enhanceable.length === 0}
          title={
            enhanceable.length === 0
              ? "Seçilen dosyalarda ölçülmüş ve güvenle düzeltilebilen bir sorun yok."
              : undefined
          }
          onClick={() =>
            void run(
              "enhance",
              enhanceable,
              (id) => enhanceMedia(id),
              (result, attempted) => ({ kind: "enhanced", result, attempted }),
            )
          }
        >
          {enhanceable.length > 0
            ? `${enhanceable.length} dosyayı iyileştir`
            : "İyileştirilebilir dosya yok"}
        </Button>
        {enhanceable.length > 0 && enhanceable.length < count && (
          <span className="faint">
            Seçilen {count} dosyanın {enhanceable.length} tanesinde ölçülmüş ve
            düzeltilebilen bir sorun var; diğerleri atlanır.
          </span>
        )}
      </div>

      {!confirmingDelete ? (
        <div className="bulkbar__row">
          <Button disabled={busy !== null} onClick={() => setConfirmingDelete(true)}>
            Seçilenleri sil
          </Button>
        </div>
      ) : (
        <div className="stack-tight" role="group" aria-label="Toplu silme onayı">
          <p className="muted">
            <strong>{count} dosya</strong> projeden kaldırılacak. Bu işlem geri alınamaz.
            Bu dosyalara bağlı kalite ve kare seçimi sonuçları da silinir; proje kapsaması
            yeniden hesaplanmalıdır.
          </p>
          <div className="row">
            <Button
              variant="primary"
              busy={busy === "delete"}
              disabled={busy !== null}
              onClick={() =>
                void run(
                  "delete",
                  selected,
                  (id) => deleteMedia(id),
                  (result, attempted) => ({ kind: "deleted", result, attempted }),
                )
              }
            >
              {count} dosyayı sil
            </Button>
            <Button disabled={busy !== null} onClick={() => setConfirmingDelete(false)}>
              Vazgeç
            </Button>
          </div>
        </div>
      )}

      {busy !== null && (
        <div className="bulkbar__row">
          {/* A real count, not a bar on a timer: this is how many files the
              server has answered for. */}
          <p className="faint" role="status">
            {progress} / {busy === "enhance" ? enhanceable.length : count} dosya işlendi
          </p>
          {/* Stops what has not started. The requests already in flight
              finish - they are not rows that can be rolled back - and the
              report says how many were left untouched. */}
          <Button onClick={() => stopper.current?.abort()}>Durdur</Button>
        </div>
      )}

      {report && <BulkReport report={report} />}
    </div>
  );
}

function BulkReport({ report }: { report: Report }) {
  const { result, attempted } = report;
  const succeeded = result.succeeded.length;
  const describe =
    report.kind === "deleted"
      ? deleteFailureText
      : report.kind === "enhanced"
        ? enhanceFailureText
        : assignFailureText;
  const reasons = groupFailures(result.failed, describe);
  const done =
    report.kind === "deleted"
      ? `${succeeded} / ${attempted} dosya silindi.`
      : report.kind === "enhanced"
        ? `${succeeded} / ${attempted} dosya iyileştirildi. Orijinaller değişmedi.`
        : `${succeeded} / ${attempted} dosya "${report.label}" olarak işaretlendi.`;
  //: Stopping is not failing. The files never sent are neither succeeded nor
  //: failed, and a report that silently dropped them would make Durdur look
  //: like it lost work.
  const untouched = result.stopped
    ? attempted - succeeded - result.failed.length
    : 0;

  return (
    <Callout tone={result.failed.length > 0 ? "caution" : "success"} title={done}>
      {result.failed.length === 0 && untouched === 0 ? (
        <span>Tümü tamamlandı.</span>
      ) : result.failed.length === 0 ? (
        <span>{untouched} dosya durduruldu ve hiç işlenmedi.</span>
      ) : (
        <div className="stack-tight">
          <span>
            {result.failed.length} dosya değişmedi ve seçili kaldı, tekrar deneyebilirsiniz.
            {untouched > 0 && ` ${untouched} dosya durduruldu ve hiç işlenmedi.`}
          </span>
          <ul className="bulkbar__reasons">
            {reasons.map((row) => (
              <li key={row.reason}>
                {row.count} dosya: {row.reason}
              </li>
            ))}
          </ul>
        </div>
      )}
    </Callout>
  );
}
